import React, { useEffect, useState } from "react";
import { Button, IconButton, Modal, Box, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SchemaFields } from "./FieldRenderer";
import {
  sectionsForArea,
  DEFAULT_SPEC_SHEET_CONFIG,
} from "./specSheetConfig";
import RoofSection from "./RoofSection";
import WallSection from "./WallSection";
import FloorSection from "./FloorSection";
import { reviseSubmission } from "./api";

const clone = (obj) => JSON.parse(JSON.stringify(obj || {}));
const noop = () => {};

const displayValue = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "object")
    return Object.entries(v)
      .map(([k, val]) => `${k}: ${val}`)
      .join("; ");
  return String(v);
};

// Build fieldId -> label lookup (including companion inputs) from the config
const buildLabelMap = (config) => {
  const map = {
    CommonHeelSize: "Common Heel Size",
    RaisedHeel: "Raised Heel Size",
  };
  (config.sections || []).forEach((section) => {
    (section.fields || []).forEach((f) => {
      map[f.id] = f.label;
      map[`${f.id}_Other`] = `${f.label} — Other`;
      map[`${f.id}Location`] = `${f.label} Locations`;
      map[`${f.id}_locations`] = `${f.label} Locations`;
    });
  });
  return map;
};

const diffFlat = (oldObj = {}, newObj = {}, labelMap, changes, skipKeys = []) => {
  const keys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);
  keys.forEach((key) => {
    if (skipKeys.includes(key)) return;
    const from = displayValue(oldObj?.[key]);
    const to = displayValue(newObj?.[key]);
    if (from !== to) {
      changes.push({ label: labelMap[key] || key, from, to });
    }
  });
};

const ReviseSubmissionModal = ({ open, submission, user, onClose, onSaved }) => {
  const config = submission?.config_snapshot || DEFAULT_SPEC_SHEET_CONFIG;
  const products = submission?.products || [];

  const [mainValues, setMainValues] = useState({});
  const [roofInfo, setRoofInfo] = useState({});
  const [wallInfo, setWallInfo] = useState({});
  const [addFloorInfo, setAddFloorInfo] = useState({});
  const [floorLevels, setFloorLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !submission) return;
    setMainValues(clone(submission.values));
    setRoofInfo(clone(submission.form_data?.roofInfo));
    setWallInfo(clone(submission.form_data?.wallInfo));
    setAddFloorInfo(clone(submission.form_data?.addFloorInfo));
    setFloorLevels(clone(submission.form_data?.floorLevels || []));
    setError(null);
  }, [open, submission]);

  if (!open || !submission) return null;

  const handleMainChange = (fieldId, value) =>
    setMainValues((prev) => ({ ...prev, [fieldId]: value }));

  // In revision mode the special fields edit as plain inputs
  const specialRenderers = {
    address: (field) => (
      <div key={field.id} className="sm:col-span-2">
        <TextField
          label={field.label}
          fullWidth
          value={mainValues.address || ""}
          onChange={(e) => handleMainChange("address", e.target.value)}
          helperText={
            mainValues.latLng
              ? `Pinned: ${Number(mainValues.latLng.lat).toFixed(5)}, ${Number(
                  mainValues.latLng.lng
                ).toFixed(5)} (pin is kept as-is)`
              : ""
          }
        />
      </div>
    ),
    date_asap: (field) => (
      <TextField
        key={field.id}
        label={field.label}
        fullWidth
        value={mainValues.requiredDate || ""}
        onChange={(e) => handleMainChange("requiredDate", e.target.value)}
        helperText='Enter a date (MM-DD-YYYY) or "ASAP"'
      />
    ),
  };

  const computeChanges = (newValues, newFormData) => {
    const labelMap = buildLabelMap(config);
    const changes = [];

    diffFlat(submission.values, newValues, labelMap, changes, ["latLng", "asap"]);
    diffFlat(submission.form_data?.roofInfo, newFormData.roofInfo, labelMap, changes);
    diffFlat(submission.form_data?.wallInfo, newFormData.wallInfo, labelMap, changes, [
      "additionalWallInfo",
    ]);
    diffFlat(submission.form_data?.addFloorInfo, newFormData.addFloorInfo, labelMap, changes);

    if (
      JSON.stringify(submission.form_data?.wallInfo?.additionalWallInfo || []) !==
      JSON.stringify(newFormData.wallInfo?.additionalWallInfo || [])
    ) {
      changes.push({
        label: "Wall Levels (framing & sheathing)",
        from: `${(submission.form_data?.wallInfo?.additionalWallInfo || []).length} level(s)`,
        to: `${(newFormData.wallInfo?.additionalWallInfo || []).length} level(s) — updated`,
      });
    }
    if (
      JSON.stringify(submission.form_data?.floorLevels || []) !==
      JSON.stringify(newFormData.floorLevels || [])
    ) {
      changes.push({
        label: "Floor Levels & Materials",
        from: `${(submission.form_data?.floorLevels || []).length} level(s)`,
        to: `${(newFormData.floorLevels || []).length} level(s) — updated`,
      });
    }
    return changes;
  };

  const handleSave = async () => {
    const newFormData = {
      floorLevels: floorLevels,
      addFloorInfo: addFloorInfo,
      wallInfo: wallInfo,
      roofInfo: roofInfo,
    };
    const changes = computeChanges(mainValues, newFormData);
    if (changes.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await reviseSubmission(submission._id, {
        values: mainValues,
        formData: newFormData,
        revisedBy: {
          id: user?.id,
          name:
            user?.name || user?.fullName || user?.displayName || user?.userID || "",
        },
        changes: changes,
      });
      onSaved(mainValues, newFormData, result.chatMessage);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="app-glass app-glass-rim absolute left-1/2 top-1/2 flex max-h-[92vh] w-[94vw] max-w-6xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl border outline-none">
        <div className="app-divider flex items-start justify-between gap-3 border-b px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-semibold">Revise Spec Sheet</h2>
            <p className="app-muted text-sm">
              Changes are logged to the internal chat with who made them and
              when.
            </p>
          </div>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-5 py-5 sm:px-6">
          {sectionsForArea(config, "main")
            .filter((s) => s.special !== "products")
            .map((section) => (
              <div key={section.id}>
                <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
                  {section.title}
                </h3>
                <SchemaFields
                  fields={section.fields}
                  values={mainValues}
                  onChange={handleMainChange}
                  columns={section.columns || 3}
                  specialRenderers={specialRenderers}
                />
              </div>
            ))}

          {products.includes("Roofs") && (
            <div className="app-divider border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Roofs</h3>
              <RoofSection
                config={config}
                isComplete={false}
                setIsComplete={noop}
                formValues={roofInfo}
                setFormValues={setRoofInfo}
              />
            </div>
          )}

          {products.includes("Wall Panels") && (
            <div className="app-divider border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Wall Panels</h3>
              <WallSection
                config={config}
                isComplete={false}
                setIsComplete={noop}
                formValues={wallInfo}
                setFormValues={setWallInfo}
              />
            </div>
          )}

          {products.includes("Floors") && (
            <div className="app-divider border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Floors</h3>
              <FloorSection
                config={config}
                isComplete={false}
                setIsComplete={noop}
                additionFieldsCompleted={false}
                setAdditionFloorFieldsCompleted={noop}
                levels={floorLevels}
                setLevels={setFloorLevels}
                formValues={addFloorInfo}
                setFormValues={setAddFloorInfo}
                quoteRequired={true}
                basicOnly={false}
              />
            </div>
          )}
        </div>

        <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3 sm:px-6">
          {error ? (
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300">{error}</p>
          ) : (
            <p className="app-muted text-sm">
              Only changed fields are recorded in the revision log.
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Revision"}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default ReviseSubmissionModal;
